/** @jsx jsx */
import { Button, useClipboard } from "@cd2/components/build/es6"
import { jsx } from "@emotion/core"
import { icons } from "../constants/icons"
import { useBroadcastChannel } from "../hooks/useBroadcastChannel"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { IRecord } from "../types/schema"
import { DropdownMenu } from "./DropdownMenu"
import { useGlobalState } from "./GlobalStateProvider"
import { themeColors } from "./EOSThemeProvider"
import React from "react"

export function MultiBranchSwitcher() {
  const switchBranch = useBranchSwitch()
  const onCopy = useClipboard()
  const alert = useSiteAlerts()
  const { currentUser } = useGlobalState()
  const branchId = currentUser.branch.id
  if (currentUser.accessibleBranches.length <= 1) {
    return null
  }
  const branchesListForSwitching = currentUser.accessibleBranches
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(branch => ({
      children: currentUser.isSuperAdmin
        ? `${branch.name} (${branch.id.slice(0, 12)}...)`
        : branch.name,
      disabled: branch.id === currentUser.branch.id,
      onClick: () => switchBranch(branch.id, { keepRoute: currentUser.isSuperAdmin }),
      displayIconSpacing: false,
    }))

  const currentBranchName = currentUser.branch.name
  return (
    <React.Fragment>
      <DropdownMenu items={branchesListForSwitching} small maxHeight="60vh">
        <Button leftIcon={icons.branches} rightIcon={icons.chevronDown} buttonColor="white">
          {currentBranchName}
        </Button>
      </DropdownMenu>
      {currentUser.isSuperAdmin && (
        <Button
          extendTheme={{ color: themeColors.lightGrey, hoverColor: themeColors.darkGrey }}
          leftIcon={icons.copy}
          buttonStyle="text"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onCopy(branchId ?? "").then(() =>
              alert.genericSuccess(`${branchId} copied to clipboard`),
            )
          }}
        >
          Copy ID: {(branchId ?? "").slice(0, 8)}...
        </Button>
      )}
    </React.Fragment>
  )
}

export function BranchSwitchButton({
  branch,
  keepRoute,
}: {
  branch: IRecord<"branches", "name">
  keepRoute?: boolean
}) {
  const switchBranch = useBranchSwitch()

  async function handleBranchClick() {
    await switchBranch(branch.id, { keepRoute })
  }

  return (
    <Button leftIcon={icons.branches} buttonColor="white" onClick={handleBranchClick}>
      Switch to {branch?.name}
    </Button>
  )
}

export function useBranchSwitch() {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const { postMessage } = useBroadcastChannel<{ message: string; options?: Record<string, any> }>(
    "branch-switcher-single",
    data => {
      console.log("received message", data)
      const path = data.options?.path || (data?.options?.keepRoute ? window.location.href : "/")
      window.location.href = path
    },
  )

  async function handleBranchClick(
    branchId: string,
    opts?: { keepRoute?: boolean; path?: string },
  ) {
    if (!currentUser.id) {
      throw new Error("Trying to switch branch with no current user")
    }
    await skeem.mutate({
      skeem_users: {
        update: {
          id: currentUser.id,
          branch: { set: branchId },
        },
      },
    })
    postMessage({ message: "Broadcast Channel: New branch selected", options: opts })
  }

  return handleBranchClick
}
